import { Reveal, ChapterLabel } from "./Reveal";

const Block = ({ index, title, children, testId }) => (
  <div data-testid={testId} className="border border-[#0B1B3D]/12">
    <div className="bg-[#0B1B3D] px-6 py-4 flex items-center justify-between">
      <h3 className="font-heading text-sm font-bold tracking-[0.18em] uppercase text-white">{title}</h3>
      <span className="font-heading text-xs font-bold text-[#C5A059]">{index}</span>
    </div>
    <div className="p-6 sm:p-8 bg-white">{children}</div>
  </div>
);

const Item = ({ label, detail }) => (
  <li className="flex gap-3 text-sm leading-relaxed">
    <span className="mt-[8px] w-3 h-px bg-[#C5A059] shrink-0" />
    <span className="text-[#4B5563]">
      <span className="font-semibold text-[#0B1B3D]">{label}</span>
      {detail && <> — {detail}</>}
    </span>
  </li>
);

export const Criteria = () => (
  <section data-testid="criteria-section" className="bg-[#F3F4F6] py-24 sm:py-32 px-6 sm:px-10">
    <div className="max-w-7xl mx-auto">
      <Reveal>
        <ChapterLabel number="03" title="Acquisition Criteria" />
        <h2 className="font-heading mt-8 text-3xl md:text-4xl font-bold tracking-tight text-[#0B1B3D] max-w-2xl">
          What we're looking for.
        </h2>
      </Reveal>

      <div className="mt-16 grid lg:grid-cols-2 gap-6 items-start">
        <div className="space-y-6">
          <Reveal>
            <Block index="3.1" title="Capabilities We're Seeking" testId="criteria-capabilities">
              <ul className="space-y-4">
                <Item label="Precision CNC Machining" detail="3, 4 & 5-axis milling, CNC turning, mill-turn, EDM" />
                <Item label="Metrology & Inspection" detail="CMM, FAI/AS9102, NIST-traceable calibration" />
                <Item label="Advanced Materials Expertise" detail="titanium, Inconel/superalloys, aerospace aluminum, stainless" />
                <Item label="Engineering Support & DFM" detail="DFM consulting, CAD/CAM — CATIA, NX, SolidWorks, Mastercam" />
              </ul>
            </Block>
          </Reveal>
          <Reveal delay={0.1}>
            <Block index="3.3" title="Company Size" testId="criteria-size">
              <p className="text-[11px] font-semibold tracking-[0.25em] uppercase text-[#4B5563]">
                Top-line revenue between
              </p>
              <p className="font-heading mt-3 text-4xl sm:text-5xl font-black tracking-tight text-[#0B1B3D]">
                $2,000,000 <span className="text-[#C5A059]">–</span> $6,000,000
              </p>
            </Block>
          </Reveal>
          <Reveal delay={0.15}>
            <Block index="3.4" title="Compliance Profile" testId="criteria-compliance">
              <ul className="space-y-4">
                <Item label="AS9100 Rev D / ISO 9001:2015 QMS" />
                <Item label="ITAR registration and/or CMMC 2.0 readiness" />
                <Item label="NADCAP special process accreditation" detail="a plus" />
              </ul>
            </Block>
          </Reveal>
        </div>

        <div className="space-y-6">
          <Reveal delay={0.05}>
            <Block index="3.2" title="Target Markets Served" testId="criteria-markets">
              <ul className="space-y-4">
                <Item label="Department of Defense" detail="direct & subcontract" />
                <Item label="Prime contractor supply chains" detail="Lockheed Martin, Raytheon, Northrop, Boeing" />
                <Item label="Naval & undersea warfare" detail="missile/munitions/guidance systems" />
                <Item label="Aerospace OEM structural" detail="avionics, UAV/UAS & space hardware" />
              </ul>
            </Block>
          </Reveal>
          <Reveal delay={0.12}>
            <Block index="3.5" title="Ideal Partner Profile" testId="criteria-partner">
              <ul className="space-y-4">
                <Item label="Established precision machine shop" detail="with aerospace/defense past performance" />
                <Item label="Documented QMS" detail="materials traceability & skilled CNC workforce" />
                <Item label="Leadership open to remaining engaged" detail="through transition" />
              </ul>
            </Block>
          </Reveal>
        </div>
      </div>

      <Reveal delay={0.1}>
        <div
          data-testid="nda-reassurance-box"
          className="mt-14 bg-white border-l-4 border-[#C5A059] border-y border-r border-y-[#0B1B3D]/12 border-r-[#0B1B3D]/12 px-8 sm:px-12 py-10"
        >
          <p className="font-serif-accent italic text-lg sm:text-xl leading-relaxed text-[#0B1B3D]">
            Representing a machine shop for sale? We move quickly with NDA-first conversations and a
            straightforward process to letter of intent.
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);
